import axios from "axios";
import { currentUserStore } from "@/stores/currentUser";
import { User } from "@/interfaces/User";
import tools from "@/composables/tools";
import router from "@/router";

export type SupervisionApp =
	| "tecnogest_front"
	| "galidancia_front"
	| "galidancia_gest";

export default function userAuth() {

	async function login(
		u?: string,
		p?: string,
		user_id?: number
	): Promise<any> {
		axios
			.get("login", {
				params: {
					email: u,
					password: p,
					user_id: user_id,
				},
			})
			.then((response) => {
				localStorage.setItem("token", response.data.access_token);
				localStorage.setItem(
					"user",
					JSON.stringify(response.data.user)
				);

				//** guardamo como state los datos del usuario logeado */
				const userJson = localStorage.getItem("user");
				const currentUserObj: User =
					userJson !== null ? JSON.parse(userJson) : {};
				const currentUser = currentUserStore();
				currentUser.update(currentUserObj);

				const store = currentUserStore();
				const saved = store.$state.redirectAfterLogin;
				store.$state.redirectAfterLogin = null;

				let destination: string;
				try {
					destination =
						saved !== null && saved !== ""
							? router.resolve(saved).href
							: router.resolve({ path: "/" }).href;
				} catch {
					destination = router.resolve({ path: "/" }).href;
				}
				location.href = destination;
			})
			.catch((error) => {
				return error.response?.status;
			});
	}

	const SUPERVISION_APP_URLS: Record<SupervisionApp, string | undefined> = {
		tecnogest_front:
			process.env.VUE_APP_PATH_TECNOGEST ||
			process.env.VUE_APP_PATH_TECNOGEST_OLD ||
			(typeof window !== "undefined" ? `${window.location.origin}/` : undefined),
		galidancia_front:
			process.env.VUE_APP_PATH_GALIDANCIA_APP || "https://app.galidancia.com/",
		galidancia_gest:
			process.env.VUE_APP_PATH_GALIDANCIA_GEST || "https://gest.galidancia.com/",
	};

	function persistAuthResponse(response: {
		access_token: string;
		user: User;
		supervision?: Record<string, unknown>;
	}): void {
		localStorage.setItem("token", response.access_token);
		localStorage.setItem("user", JSON.stringify(response.user));
		const currentUser = currentUserStore();
		currentUser.update(response.user);
		if (response.supervision) {
			localStorage.setItem("supervision", JSON.stringify(response.supervision));
		} else {
			localStorage.removeItem("supervision");
		}
	}

	function buildSupervisionLoginUrl(base: string, code: string): string {
		const normalized = base.endsWith("/") ? base : `${base}/`;
		return `${normalized}login?sc=${encodeURIComponent(code)}`;
	}

	async function loginSupervisionCode(code: string): Promise<void> {
		try {
			const response = await axios.post("supervision/redeem", {
				code,
				app: "tecnogest_front",
			});
			persistAuthResponse(response.data);
			const store = currentUserStore();
			const saved = store.$state.redirectAfterLogin;
			store.$state.redirectAfterLogin = null;
			let destination = "/";
			try {
				destination =
					saved !== null && saved !== ""
						? router.resolve(saved).href
						: router.resolve({ path: "/" }).href;
			} catch {
				destination = router.resolve({ path: "/" }).href;
			}
			location.href = destination;
		} catch (error: any) {
			tools().toast(
				error?.response?.data?.message ||
					"No se pudo activar el modo supervisión",
				"danger",
				4000
			);
		}
	}

	async function openSupervision(
		user_id: number,
		app: SupervisionApp
	): Promise<void> {
		const base = SUPERVISION_APP_URLS[app];
		if (!base) {
			tools().toast(
				`URL no configurada para ${app}. Revisa las variables VUE_APP_PATH_*`,
				"danger",
				5000
			);
			return;
		}

		try {
			const response = await axios.post("supervision/request", {
				user_id,
				app,
			});
			tools().setLog(
				`Supervisión solicitada para user #${user_id} en ${app}`,
				"warning"
			);
			window.open(buildSupervisionLoginUrl(base, response.data.code));
		} catch (error: any) {
			tools().toast(
				error?.response?.data?.message ||
					"No se pudo generar el acceso de supervisión",
				"danger",
				4000
			);
		}
	}

	/** @deprecated Usar openSupervision(user_id, 'galidancia_front') */
	async function loginGalidancia(user_id: number): Promise<void> {
		await openSupervision(user_id, "galidancia_front");
	}

	function logout(): void {
		tools().setLog("Cerró sesión", "info");
		console.log("Cerrando sesion");
		localStorage.removeItem("token");
		localStorage.removeItem("user");
		localStorage.removeItem("supervision");
		location.href = "login";

		//window.location.reload()
	}

	async function checkEmailTlf(email_tlf: string): Promise<any> {
		try {
			const response = await axios.get("checkEmailTlf", {
				params: {
					email: email_tlf,
				},
			});
			return response.data;
		} catch (error) {
			tools().toast(
				"Error en el sistema de autenticación. Consulta con el administrador #145",
				"danger",
				5000
			);
		}
	}

	/*registra a una entidad que no existe como usuario del sistema*/
	async function registerFromEntidad(
		phone_email: string,
		pw: string,
		entidad_id: number
	) {
		try {
			await axios.get("registerFromEntidad", {
				params: {
					email: phone_email,
					pw: pw,
					entidad_id: entidad_id,
				},
			});
			login(phone_email, pw);
		} catch (error) {
			tools().toast(
				"No hemos podido registrarte. Consultao con el administrador #146",
				"danger",
				5000
			);
		}
	}

	/* Devuelve true o false si tiene o no acceso al recurso basandose en los permisos */
	function can(permission: string): boolean {
		try {
			if (currentUserStore().$state.user !== undefined)
				return currentUserStore().$state.user?.permissions.includes(
					permission
				)
					? true
					: false;
			else return false;
		} catch (error) {
			console.log("Error en la comprobación de permisos");
			return false;
		}

	}

	function hasRole(roleName: string): boolean {
		try {
			const user = currentUserStore().$state.user;
			if (!user) return false;

			if (user.roles?.length) {
				return user.roles.some((role) => {
					const name = typeof role === 'string' ? role : role?.name;
					return name === roleName;
				});
			}

			// Sesiones antiguas sin roles en localStorage: cuenta sistema #19
			if (roleName === 'administrador' && user.id === 19) {
				return true;
			}

			return false;
		} catch (error) {
			console.log("Error en la comprobación de roles");
			return false;
		}
	}

	/* devuelve true o false si hay algun usuario logeado */
	function isLogged(): boolean {
		return localStorage.getItem("user") != "" ? true : false;
	}

	return {
		login,
		logout,
		can,
		hasRole,
		isLogged,
		checkEmailTlf,
		registerFromEntidad,
		loginGalidancia,
		openSupervision,
		loginSupervisionCode,
	};
}
